import React from "react";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/ui/button";
import type { ResetChannel } from "@/lib/api";
import { useT } from "@/app/localization";

/**
 * Shared "Reset password" channel logic, reused by the admin (User Details) and
 * self-service (UserMenu) UIs. Returns a trigger handler + a stable dialog node.
 *
 * Availability + masked destinations come from the BACKEND
 * (/reset-availability), which applies the authoritative eligibility rule
 * (email present; phone present + verified + valid E.164 + allowed country). The
 * client never re-derives eligibility or handles full destinations.
 *
 * - both available → masked channel-selection dialog before sending
 * - one available  → initiate it directly (no unnecessary dialog)
 * - neither        → `disabled` true with an explanatory `title`
 *
 * The dialog node is returned separately so callers can render it OUTSIDE a
 * dropdown menu (which unmounts on close).
 */
export function useResetPassword({
  emailAvailable,
  smsAvailable,
  maskedEmail,
  maskedPhone,
  loading = false,
  error = false,
  initiate,
}: {
  emailAvailable: boolean;
  smsAvailable: boolean;
  maskedEmail?: string | null;
  maskedPhone?: string | null;
  /** Authoritative availability is still loading — block initiation. */
  loading?: boolean;
  /** Availability request failed — block initiation. */
  error?: boolean;
  initiate: (channel: ResetChannel) => Promise<void>;
}) {
  const t = useT();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const none = !emailAvailable && !smsAvailable;
  // Initiation is only allowed once authoritative availability has loaded OK and
  // at least one method exists.
  const blocked = loading || error || none;

  async function run(channel: ResetChannel) {
    setSending(true);
    try {
      await initiate(channel);
      setDialogOpen(false);
    } finally {
      setSending(false);
    }
  }

  function onTrigger() {
    if (blocked || sending) return;
    if (emailAvailable && smsAvailable) setDialogOpen(true);
    else void run(emailAvailable ? "email" : "sms");
  }

  // Distinct states — loading and error must NOT look like "no methods".
  let label: string;
  let title: string;
  if (loading) {
    label = t("operator.auth.reset_options_checking");
    title = label;
  } else if (error) {
    label = t("operator.auth.reset_options_failed");
    title = label;
  } else if (none) {
    label = t("operator.auth.reset_password");
    title = t("operator.auth.reset_no_methods");
  } else {
    label = sending ? t("operator.auth.sending") : t("operator.auth.reset_password");
    title = t("operator.auth.reset_send_title");
  }

  const dialog = (
    <Modal
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      title={t("operator.auth.reset_choose_channel")}
    >
      <div className="space-y-3">
        <Button className="w-full justify-start" variant="outline"
                disabled={sending} onClick={() => void run("email")}>
          {t("operator.auth.reset_via_email", { destination: maskedEmail ?? "" })}
        </Button>
        <Button className="w-full justify-start" variant="outline"
                disabled={sending} onClick={() => void run("sms")}>
          {t("operator.auth.reset_via_sms", { destination: maskedPhone ?? "" })}
        </Button>
        {/* Consent reminder — the permanent opt-in still comes from Account
            Settings; choosing this channel does not create consent. */}
        <p className="px-1 text-xs opacity-60">
          {t("operator.auth.reset_sms_notice")}
        </p>
        <Button className="w-full" variant="ghost"
                disabled={sending} onClick={() => setDialogOpen(false)}>
          {t("common.action.cancel")}
        </Button>
      </div>
    </Modal>
  );

  return {
    onTrigger,
    dialog,
    disabled: blocked || sending,
    title,
    label,
  };
}
